import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// API helper: JSON in/out, throws Error(message-from-server) so callers can toast it
// ---------------------------------------------------------------------------
export function useAdminApi() {
  const navigate = useNavigate();
  return useCallback(async (path, { method = 'GET', body } = {}) => {
    const response = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      // empty or non-JSON body
    }

    if (response.status === 401) {
      navigate('/login');
      throw new Error((data && data.message) || 'Please log in again.');
    }
    if (!response.ok) {
      const error = new Error((data && (data.message || data.error)) || `Request failed (${response.status})`);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  }, [navigate]);
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------
const ToastContext = createContext(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => setToasts((current) => current.filter((t) => t.id !== id)), []);
  const toast = useCallback((message, type = 'success') => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, message, type }]);
    setTimeout(() => dismiss(id), type === 'error' ? 9000 : 4500);
  }, [dismiss]);

  const styles = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-gray-800',
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-4 right-4 z-[90] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`${styles[t.type] || styles.info} text-white text-sm rounded-lg shadow-lg px-4 py-3 flex items-start gap-3`}>
            <span className="flex-1 break-words">{t.message}</span>
            <button onClick={() => dismiss(t.id)} className="opacity-70 hover:opacity-100" aria-label="Dismiss">✕</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Confirm dialog: `await confirm({ title, message, confirmLabel, danger, requireText })`
// ---------------------------------------------------------------------------
const ConfirmContext = createContext(() => Promise.resolve(false));
export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [typed, setTyped] = useState('');

  const confirm = useCallback((options) => new Promise((resolve) => {
    setTyped('');
    setDialog({ ...options, resolve });
  }), []);

  const close = (result) => {
    dialog.resolve(result);
    setDialog(null);
  };

  const canConfirm = !dialog || !dialog.requireText || typed.trim() === dialog.requireText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" onClick={() => close(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="text-lg font-semibold mb-2">{dialog.title || 'Are you sure?'}</h3>
            {dialog.message && <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{dialog.message}</p>}
            {dialog.requireText && (
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">
                  Type <span className="font-mono font-semibold">{dialog.requireText}</span> to confirm
                </label>
                <input
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => close(false)} className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50">
                Cancel
              </button>
              <button
                disabled={!canConfirm}
                onClick={() => close(true)}
                className={`px-4 py-2 text-sm rounded text-white disabled:opacity-40 ${dialog.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-900 hover:bg-black'}`}
              >
                {dialog.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
const BADGE_COLORS = {
  gray: 'bg-gray-100 text-gray-700',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  orange: 'bg-orange-100 text-orange-800',
  amber: 'bg-amber-100 text-amber-800',
  blue: 'bg-blue-100 text-blue-800',
  purple: 'bg-purple-100 text-purple-800',
};

export function Badge({ color = 'gray', children, title }) {
  return (
    <span title={title} className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${BADGE_COLORS[color] || BADGE_COLORS.gray}`}>
      {children}
    </span>
  );
}

const STATUS_COLORS = { new: 'orange', active: 'green', review: 'blue', paused: 'gray', rejected: 'red' };
export const StatusBadge = ({ status }) => <Badge color={STATUS_COLORS[status] || 'gray'}>{status || 'unknown'}</Badge>;

export function Card({ title, actions, children, className = '' }) {
  return (
    <section className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      {(title || actions) && (
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Button({ variant = 'default', busy, disabled, className = '', children, ...props }) {
  const variants = {
    default: 'bg-white border border-gray-300 text-gray-800 hover:bg-gray-50',
    primary: 'bg-gray-900 text-white hover:bg-black border border-gray-900',
    danger: 'bg-white border border-red-300 text-red-700 hover:bg-red-50',
    success: 'bg-green-600 text-white hover:bg-green-700 border border-green-600',
  };
  return (
    <button
      disabled={disabled || busy}
      className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
      {...props}
    >
      {busy && <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />}
      {children}
    </button>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
    </label>
  );
}

export const inputClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white';

// "5m ago", "3h ago", "2d ago"
export function timeAgo(value) {
  if (!value) return 'never';
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export const formatDateTime = (value) => (value ? new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '-');

// Searchable select: type to filter, click to pick. options: [{ value, label, sub }]
export function Combobox({ options, value, onChange, placeholder = 'Search…' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => options.find((o) => String(o.value) === String(value)), [options, value]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? options.filter((o) => `${o.label} ${o.sub || ''}`.toLowerCase().includes(q)) : options;
    return list.slice(0, 8);
  }, [options, query]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <div className="relative">
      <input
        value={open ? query : (selected ? selected.label : '')}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        className={inputClass}
      />
      {open && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
          {matches.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">No matches</li>}
          {matches.map((option) => (
            <li
              key={option.value}
              onMouseDown={(e) => { e.preventDefault(); onChange(option.value); setOpen(false); }}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
            >
              {option.label}
              {option.sub && <span className="text-gray-500"> · {option.sub}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
